// Turning an S3 object URL into something S3 will actually serve.
//
// The objects behind these links are private, so a plain GET of the URL comes
// back 403 — every one of them has to be presigned first. That is the whole
// reason this exists as a shared helper rather than being written out at each
// call site.

import { GetObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

export type S3Credentials = {
  region?: string | null;
  accessKeyId?: string | null;
  secretAccessKey?: string | null;
};

/**
 * Split an S3 URL into bucket + key, covering both addressing styles:
 *   virtual-hosted  <bucket>.s3.<region>.amazonaws.com/<key>
 *   path-style      s3.<region>.amazonaws.com/<bucket>/<key>
 *
 * The bucket has to come from the link itself. Signing the key against whatever
 * bucket the app happens to be configured with produces a perfectly valid
 * signature for an object that isn't there, because these documents can live in a
 * different bucket than the one the API reports.
 *
 * Returns an empty bucket when none can be read out, which is the caller's cue to
 * fall back to a configured one.
 */
export const parseS3Location = (
  link: string
): { bucket: string; key: string } => {
  try {
    const url = new URL(link);
    const rawPath = url.pathname.replace(/^\/+/, '');
    const hosted = url.host.match(/^(.+?)\.s3[.-]/);
    if (hosted) {
      return { bucket: hosted[1], key: decodeURIComponent(rawPath) };
    }
    if (/^s3[.-]/.test(url.host)) {
      const slash = rawPath.indexOf('/');
      if (slash > 0) {
        return {
          bucket: rawPath.slice(0, slash),
          key: decodeURIComponent(rawPath.slice(slash + 1))
        };
      }
    }
    return { bucket: '', key: decodeURIComponent(rawPath) };
  } catch {
    return { bucket: '', key: '' };
  }
};

/** Default signature lifetime — long enough to click, short enough not to leak. */
const DEFAULT_EXPIRES_S = 60 * 60;

/**
 * Presign an S3 object URL for reading.
 *
 * Returns null rather than throwing: every caller has a sensible fallback (show
 * an error state, or hand the raw link to a viewer that will fail visibly), and
 * none of them wants an exception mid-render.
 *
 * `downloadAs` sets Content-Disposition on the response, which is what actually
 * makes a browser save the file instead of opening it in a tab — the HTML
 * `download` attribute is ignored for cross-origin URLs, so it cannot do this job.
 */
export const presignS3Url = async (
  link: string,
  credentials: S3Credentials,
  options?: { fallbackBucket?: string | null; expiresIn?: number; downloadAs?: string }
): Promise<string | null> => {
  const { bucket, key } = parseS3Location(link);
  const resolvedBucket = bucket || options?.fallbackBucket || '';
  if (!key || !resolvedBucket) return null;
  if (!credentials?.accessKeyId || !credentials?.secretAccessKey) return null;

  try {
    const client = new S3Client({
      region: credentials.region || undefined,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey
      }
    });
    return await getSignedUrl(
      client,
      new GetObjectCommand({
        Bucket: resolvedBucket,
        Key: key,
        ...(options?.downloadAs
          ? {
              ResponseContentDisposition: `attachment; filename="${options.downloadAs.replace(
                /"/g,
                ''
              )}"`
            }
          : {})
      }),
      { expiresIn: options?.expiresIn ?? DEFAULT_EXPIRES_S }
    );
  } catch {
    return null;
  }
};
